import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  HelpCircle, 
  Download, 
  Search, 
  ChevronDown, 
  ChevronUp,
  BookOpen,
  Package,
  ArrowLeftRight,
  FileText,
  User,
  Shield,
  Calendar,
  BarChart3,
  Settings,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Categorías de preguntas
const FAQ_CATEGORIES = [
  {
    id: 'general',
    title: 'General',
    icon: HelpCircle,
    color: 'bg-blue-500'
  },
  {
    id: 'login',
    title: 'Inicio de Sesión',
    icon: User,
    color: 'bg-green-500'
  },
  {
    id: 'inventory',
    title: 'Inventario',
    icon: Package,
    color: 'bg-purple-500'
  },
  {
    id: 'import',
    title: 'Importación',
    icon: FileText,
    color: 'bg-emerald-500'
  },
  {
    id: 'movements',
    title: 'Movimientos',
    icon: ArrowLeftRight,
    color: 'bg-orange-500'
  },
  {
    id: 'reports',
    title: 'Reportes',
    icon: BarChart3,
    color: 'bg-teal-500'
  },
  {
    id: 'profile',
    title: 'Perfil y Seguridad',
    icon: Shield,
    color: 'bg-red-500'
  },
  {
    id: 'admin',
    title: 'Administración',
    icon: Settings,
    color: 'bg-gray-500'
  },
  {
    id: 'trash',
    title: 'Papelera',
    icon: AlertTriangle,
    color: 'bg-amber-500'
  },
  {
    id: 'etiquetas',
    title: 'Etiquetas y Códigos',
    icon: Package,
    color: 'bg-cyan-500'
  },
  {
    id: 'calendar',
    title: 'Calendario',
    icon: Calendar,
    color: 'bg-indigo-500'
  }
];

// Preguntas frecuentes completas
const FAQ_DATA = [
  // General
  {
    category: 'general',
    question: '¿Qué es el Sistema de Entradas y Salidas de Equipo?',
    answer: 'Es una plataforma web diseñada para CM Idiomas que permite gestionar el inventario de equipos, registrar préstamos (salidas) y devoluciones (retornos) de equipos para eventos, generar reportes y mantener un control detallado de todos los activos de la empresa.'
  },
  {
    category: 'general',
    question: '¿Qué navegadores son compatibles?',
    answer: 'El sistema es compatible con las versiones más recientes de Google Chrome, Mozilla Firefox, Microsoft Edge y Safari. Recomendamos usar Google Chrome para la mejor experiencia.'
  },
  {
    category: 'general',
    question: '¿Puedo usar el sistema desde mi celular?',
    answer: 'Sí, el sistema es responsivo y se adapta a dispositivos móviles. Sin embargo, para una mejor experiencia al realizar tareas complejas como crear movimientos o generar reportes, recomendamos usar una computadora.'
  },
  {
    category: 'general',
    question: '¿El sistema funciona sin conexión a internet?',
    answer: 'No, el sistema requiere conexión a internet para funcionar ya que los datos se almacenan en la nube. Esto garantiza que la información esté siempre actualizada y disponible desde cualquier lugar.'
  },
  {
    category: 'general',
    question: '¿Dónde puedo descargar el manual de usuario?',
    answer: 'Puedes descargar el manual completo en PDF desde esta misma página, usando el botón "Descargar Manual" en la parte superior. El manual incluye instrucciones detalladas de todas las funciones del sistema.'
  },

  // Login
  {
    category: 'login',
    question: '¿Cómo inicio sesión en el sistema?',
    answer: 'Ingresa a la dirección web del sistema, escribe tu nombre de usuario y contraseña en los campos correspondientes, y haz clic en "Ingresar". Si es tu primera vez, el administrador te proporcionará tus credenciales.'
  },
  {
    category: 'login',
    question: '¿Qué hago si olvidé mi contraseña?',
    answer: 'En la pantalla de inicio de sesión, haz clic en "¿Olvidaste tu contraseña?". Ingresa tu nombre de usuario y podrás recuperarla mediante tu pregunta secreta (si la configuraste) o solicitar un enlace de recuperación por correo electrónico.'
  },
  {
    category: 'login',
    question: '¿Por qué no puedo iniciar sesión?',
    answer: 'Verifica que: 1) Tu nombre de usuario y contraseña sean correctos (distingue mayúsculas), 2) Tu cuenta esté activa (no suspendida), 3) No tengas problemas de conexión a internet. Si el problema persiste, contacta al administrador.'
  },
  {
    category: 'login',
    question: '¿Cuánto tiempo dura mi sesión activa?',
    answer: 'Tu sesión permanece activa mientras uses el sistema. Por seguridad, después de un período de inactividad prolongado, el sistema te pedirá volver a iniciar sesión.'
  },
  {
    category: 'login',
    question: '¿Puedo tener mi sesión abierta en varios dispositivos?',
    answer: 'Sí, puedes iniciar sesión desde múltiples dispositivos simultáneamente. Sin embargo, por seguridad, te recomendamos cerrar sesión cuando termines de usar el sistema en dispositivos compartidos.'
  },

  // Inventario
  {
    category: 'inventory',
    question: '¿Cómo puedo ver el inventario disponible?',
    answer: 'En el menú lateral, haz clic en "Inventario". Verás una tabla con todos los tipos de equipo, su cantidad total, cuántos están asignados y cuántos disponibles. Puedes usar los filtros para buscar equipos específicos.'
  },
  {
    category: 'inventory',
    question: '¿Qué significa cada estado de los equipos?',
    answer: 'Los equipos pueden tener los siguientes estados: "Disponible" (listo para préstamo), "Asignado" (actualmente prestado), "En Mantenimiento" (en reparación), "Dañado" (no funcional). Solo los equipos "Disponibles" pueden ser prestados.'
  },
  {
    category: 'inventory',
    question: '¿Cómo veo los números de serie de los equipos?',
    answer: 'En la tabla de inventario, haz clic en la flecha (▶) junto al nombre del equipo para expandirlo. Verás todas las unidades individuales con su número de serie, ubicación actual y estado.'
  },
  {
    category: 'inventory',
    question: '¿Por qué no veo un equipo en la lista de salida?',
    answer: 'El sistema solo muestra equipos que tienen stock disponible en la ubicación seleccionada. Si no ves un equipo, verifica en Inventario si hay unidades disponibles en esa ubicación o si todas están asignadas.'
  },
  {
    category: 'inventory',
    question: '¿Cómo sé en qué ubicación está un equipo específico?',
    answer: 'En Inventario, expande el equipo haciendo clic en la flecha. Cada unidad muestra su ubicación actual. También puedes generar el reporte "Inventario con Series" que incluye la ubicación de cada número de serie.'
  },
  {
    category: 'inventory',
    question: '¿Puedo agregar nuevos equipos al inventario?',
    answer: 'Solo los usuarios con permisos de administrador o almacenista pueden agregar nuevos equipos. Si necesitas agregar equipo, contacta al administrador del sistema.'
  },

  // Importación
  {
    category: 'import',
    question: '¿Cómo puedo agregar muchos equipos de una sola vez?',
    answer: 'Usa la importación masiva: 1) Ve a "Plantillas" en el menú lateral, 2) Descarga la plantilla de unidades, 3) Llena los datos en Excel, 4) Ve a Inventario → Importar y sube el archivo. El sistema agregará todas las unidades automáticamente.'
  },
  {
    category: 'import',
    question: '¿Qué datos necesito para importar equipos?',
    answer: 'Solo 3 columnas son obligatorias: "Nombre_Equipo" (el tipo de equipo), "Número_Serie" (único para cada unidad) y "Ubicación". El resto de campos son opcionales y se generan automáticamente si los dejas vacíos.'
  },
  {
    category: 'import',
    question: '¿Cómo agrego unidades a un equipo que ya existe?',
    answer: 'En tu archivo Excel, usa el nombre exacto del equipo en "Nombre_Equipo" (ej: "MacBook Pro 14"). El sistema buscará el equipo por nombre y agregará las nuevas unidades al conjunto existente.'
  },
  {
    category: 'import',
    question: '¿Debo llenar los campos ID_Unidad e ID_Equipo?',
    answer: 'No, déjalos vacíos. El sistema genera automáticamente los IDs únicos. Solo necesitas llenarlos si quieres actualizar unidades existentes (lo cual no es común).'
  },
  {
    category: 'import',
    question: '¿Qué pasa si importo un número de serie que ya existe?',
    answer: 'El sistema detectará el duplicado y omitirá esa fila. Al final de la importación, te mostrará cuántas unidades se crearon, actualizaron y omitieron por duplicado.'
  },
  {
    category: 'import',
    question: '¿Puedo crear categorías y ubicaciones nuevas al importar?',
    answer: 'Sí, si escribes una categoría o ubicación que no existe, el sistema la creará automáticamente. Por ejemplo, si pones "Sala Nueva" como ubicación, se creará si no existía.'
  },
  {
    category: 'import',
    question: '¿Dónde encuentro las plantillas de importación?',
    answer: 'Ve a "Plantillas" en el menú principal. Ahí encontrarás todas las plantillas descargables y guías paso a paso para cada tipo de importación.'
  },

  // Movimientos
  {
    category: 'movements',
    question: '¿Cómo registro una salida de equipo?',
    answer: 'Ve a "Movimientos" y haz clic en "+ Nueva Salida". Completa los datos del evento (nombre, cliente, fecha, responsable), selecciona la ubicación de origen, agrega los equipos que necesitas con sus cantidades, y haz clic en "Crear Salida".'
  },
  {
    category: 'movements',
    question: '¿Cómo registro el retorno de equipos?',
    answer: 'En la lista de movimientos, busca la salida que deseas retornar y haz clic en el botón "Retornar". Indica la cantidad de equipos según su estado: buenos, con fallas, o perdidos. La suma debe igualar el total prestado.'
  },
  {
    category: 'movements',
    question: '¿Qué pasa si un equipo se pierde o daña?',
    answer: 'Al registrar el retorno, marca la cantidad correspondiente como "Con Fallas" o "Perdidos". Los equipos perdidos se descontarán automáticamente del inventario. Los equipos con fallas quedarán marcados para revisión.'
  },
  {
    category: 'movements',
    question: '¿Puedo modificar una salida después de crearla?',
    answer: 'Sí, siempre que la salida no haya sido retornada completamente. Busca el movimiento en la lista y haz clic en el botón de editar (lápiz). Podrás modificar los datos del evento y los equipos.'
  },
  {
    category: 'movements',
    question: '¿Cómo descargo la responsiva de un movimiento?',
    answer: 'En la lista de movimientos, encuentra el movimiento deseado y haz clic en el botón con icono de PDF. El documento se descargará automáticamente e incluye todos los datos del evento, equipos prestados y espacios para firmas.'
  },
  {
    category: 'movements',
    question: '¿Puedo hacer retornos parciales?',
    answer: 'Sí, puedes retornar solo una parte de los equipos prestados. El sistema mantendrá el movimiento como "pendiente" hasta que se retornen todos los equipos.'
  },
  {
    category: 'movements',
    question: '¿Cómo veo los movimientos pendientes de retorno?',
    answer: 'En el Dashboard verás el KPI "Pendientes de Retorno" con la cantidad total. También puedes filtrar la lista de movimientos por estado "Pendiente" en la página de Movimientos.'
  },

  // Reportes
  {
    category: 'reports',
    question: '¿Qué tipos de reportes puedo generar?',
    answer: 'El sistema ofrece 4 tipos de reportes: 1) Inventario general, 2) Inventario con números de serie y ubicaciones, 3) Historial de movimientos, 4) Equipos por responsable. Todos disponibles en PDF y Excel.'
  },
  {
    category: 'reports',
    question: '¿Cómo genero un reporte en PDF?',
    answer: 'Ve a "Reportes", selecciona los filtros que desees (equipo específico, incluir números de serie), y haz clic en el botón del tipo de reporte que necesitas en la sección "Exportar a PDF". El archivo se descargará automáticamente.'
  },
  {
    category: 'reports',
    question: '¿Cómo exporto datos a Excel?',
    answer: 'En la página de Reportes, busca la sección "Exportar a Excel" y haz clic en el botón del tipo de reporte. Se descargará un archivo .xlsx que puedes abrir con Excel, Google Sheets o cualquier programa compatible.'
  },
  {
    category: 'reports',
    question: '¿Los reportes incluyen la ubicación de los equipos?',
    answer: 'Sí, los reportes de inventario ahora incluyen una columna "Ubicaciones" que muestra dónde está cada equipo. Si eliges "Incluir números de serie", verás la ubicación exacta de cada unidad individual.'
  },
  {
    category: 'reports',
    question: '¿Puedo filtrar los reportes por fechas?',
    answer: 'Sí, el reporte de movimientos permite filtrar por rango de fechas. Selecciona la fecha inicial y final antes de generar el reporte.'
  },

  // Perfil y Seguridad
  {
    category: 'profile',
    question: '¿Cómo cambio mi contraseña?',
    answer: 'Ve a "Mi Perfil" desde el menú de usuario (esquina superior derecha). En la sección "Cambiar Contraseña", ingresa tu contraseña actual, la nueva contraseña y confírmala. Haz clic en "Cambiar Contraseña".'
  },
  {
    category: 'profile',
    question: '¿Qué es la pregunta de seguridad y por qué debo configurarla?',
    answer: 'La pregunta de seguridad es un método de recuperación de contraseña. Si olvidas tu contraseña, podrás restablecerla respondiendo correctamente tu pregunta secreta. Te recomendamos configurarla en "Mi Perfil".'
  },
  {
    category: 'profile',
    question: '¿Cómo configuro mi pregunta de seguridad?',
    answer: 'En "Mi Perfil", busca la sección "Pregunta de Seguridad". Selecciona una pregunta del menú desplegable, escribe tu respuesta (recuérdala bien) y haz clic en "Guardar Pregunta".'
  },
  {
    category: 'profile',
    question: '¿Puedo cambiar mi foto de perfil?',
    answer: 'Sí, en "Mi Perfil" puedes agregar una URL de imagen para tu foto de perfil. La imagen debe estar alojada en internet (por ejemplo, en un servicio como Imgur o Google Drive con enlace público).'
  },
  {
    category: 'profile',
    question: '¿Cómo configuro las notificaciones por correo?',
    answer: 'En "Mi Perfil", busca la sección "Preferencias de Notificaciones". Activa o desactiva las notificaciones que desees recibir: nuevas salidas, retornos, equipos perdidos, equipos con fallas.'
  },

  // Administración
  {
    category: 'admin',
    question: '¿Quién puede crear nuevos usuarios?',
    answer: 'Solo los usuarios con rol de Administrador o Super Administrador pueden crear, modificar o eliminar usuarios del sistema.'
  },
  {
    category: 'admin',
    question: '¿Qué roles de usuario existen?',
    answer: 'El sistema tiene 6 roles: Super Admin (acceso total), Admin (gestión completa), Supervisor (supervisión de operaciones), Auditor (solo lectura), Almacenista (gestión de inventario), Usuario (operaciones básicas).'
  },
  {
    category: 'admin',
    question: '¿Se puede personalizar qué puede hacer cada rol?',
    answer: 'Sí, los Super Administradores pueden modificar los permisos de cada rol desde la página "Permisos". También pueden asignar permisos personalizados a usuarios específicos.'
  },
  {
    category: 'admin',
    question: '¿Cómo veo el historial de acciones en el sistema?',
    answer: 'Los administradores pueden acceder a "Auditoría" desde el menú. Allí verán un registro detallado de todas las acciones realizadas: quién, qué, cuándo y desde dónde.'
  },
  {
    category: 'admin',
    question: '¿Puedo personalizar el aspecto del sistema?',
    answer: 'Los Super Administradores pueden personalizar colores, logos y textos del sistema desde la página "Estilo" en el menú de administración.'
  },
  {
    category: 'admin',
    question: '¿Cómo funciona el sistema de Backup?',
    answer: 'Desde "Backup" en administración, puedes crear respaldos manuales o programar backups automáticos. También puedes vincular tu cuenta de OneDrive para almacenar los respaldos en la nube automáticamente.'
  },
  {
    category: 'admin',
    question: '¿Cómo envío anuncios a los usuarios?',
    answer: 'Ve a "Anuncios" en el menú de administración. Puedes crear anuncios que aparecerán como modales al iniciar sesión y también generarán una notificación en la campana de cada usuario.'
  },
  {
    category: 'admin',
    question: '¿Para qué sirve el Checklist/QA?',
    answer: 'En "Debugger" encontrarás un checklist de pruebas del sistema. Puedes ejecutarlo, ir marcando los items completados, guardar progreso parcial y retomarlo después. Es útil para verificar que todo funciona correctamente tras actualizaciones.'
  },
  {
    category: 'trash',
    question: '¿Qué pasa cuando elimino un equipo, categoría o ubicación?',
    answer: 'Los elementos no se borran permanentemente de inmediato. Van a una "Papelera" donde puedes restaurarlos si fue un error, o eliminarlos permanentemente si realmente ya no los necesitas.'
  },
  {
    category: 'trash',
    question: '¿Cómo restauro un elemento de la papelera?',
    answer: 'En la página correspondiente (Inventario, Categorías, Ubicaciones, Usuarios, Responsables), cambia a la pestaña "Papelera". Ahí verás los elementos eliminados con botones para "Restaurar" o "Eliminar Permanentemente".'
  },
  {
    category: 'trash',
    question: '¿Puedo vaciar toda la papelera de una vez?',
    answer: 'Sí, en la vista de papelera hay un botón "Vaciar Papelera" que elimina permanentemente todos los elementos. Esta acción no se puede deshacer.'
  },
  {
    category: 'etiquetas',
    question: '¿Cómo genero etiquetas QR o códigos de barras?',
    answer: 'Ve a "Etiquetas QR" en el menú principal. Puedes seleccionar equipos y generar etiquetas con código QR, código de barras, o ambos. Las etiquetas se pueden imprimir directamente o descargar como PDF.'
  },
  {
    category: 'etiquetas',
    question: '¿Puedo escanear códigos QR para buscar equipos?',
    answer: 'Sí, en la sección de Inventario hay un botón de escaneo QR que activa la cámara de tu dispositivo. Al escanear un código, se abre directamente la información del equipo correspondiente.'
  },
  {
    category: 'calendar',
    question: '¿Qué muestra el calendario?',
    answer: 'El calendario muestra todas las salidas y retornos programados, préstamos activos, y fechas de vencimiento. Puedes ver por mes, semana o día, y hacer clic en un evento para ver los detalles del movimiento.'
  },
  {
    category: 'calendar',
    question: '¿Cómo veo los préstamos que vencen pronto?',
    answer: 'En el Dashboard y en el Calendario puedes ver alertas de préstamos próximos a vencer. El sistema también envía notificaciones automáticas cuando un préstamo está por vencer o ya venció.'
  },
  {
    category: 'movements',
    question: '¿Qué es el resumen de devolución en el modal de retorno?',
    answer: 'Al registrar un retorno, antes de confirmar verás un resumen visual que muestra exactamente cuántas unidades se registrarán como "Bueno", "Con Falla" y "Perdido". Esto te permite verificar que las cantidades sean correctas antes de confirmar.'
  },
  {
    category: 'profile',
    question: '¿Cómo cambio el tema visual del sistema?',
    answer: 'En tu perfil de usuario, encontrarás la opción de seleccionar un tema de color. El cambio se aplica inmediatamente a toda la interfaz y se guarda para tu próximo inicio de sesión.'
  }
];

const FAQPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedQuestions, setExpandedQuestions] = useState({});

  // Get user role from localStorage or context
  const getUserRole = () => {
    try {
      const storedUser = localStorage.getItem('cm_idiomas_user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        return user.role || 'user';
      }
    } catch (e) {}
    return 'user';
  };

  const userRole = getUserRole();

  // Define which categories are visible to which roles
  const categoryPermissions = {
    general: ['user', 'supervisor', 'admin', 'superadmin', 'almacenista', 'auditor'],
    login: ['user', 'supervisor', 'admin', 'superadmin', 'almacenista', 'auditor'],
    inventory: ['user', 'supervisor', 'admin', 'superadmin', 'almacenista'],
    movements: ['user', 'supervisor', 'admin', 'superadmin', 'almacenista'],
    reports: ['supervisor', 'admin', 'superadmin', 'auditor'],
    profile: ['user', 'supervisor', 'admin', 'superadmin', 'almacenista', 'auditor'],
    admin: ['admin', 'superadmin']
  };

  // Filter categories based on user role
  const visibleCategories = FAQ_CATEGORIES.filter(cat => 
    categoryPermissions[cat.id]?.includes(userRole)
  );

  const toggleQuestion = (index) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleDownloadManual = async () => {
    try {
      const response = await fetch(`${API_URL}/api/manual/download`);
      if (!response.ok) throw new Error('Error al descargar');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Manual_Usuario_CM_Idiomas.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Manual descargado exitosamente');
    } catch (error) {
      toast.error('Error al descargar el manual');
    }
  };

  // Filter FAQs based on user role and search/category filters
  const filteredFAQs = FAQ_DATA.filter(faq => {
    // First check if the category is visible to this role
    const categoryVisible = categoryPermissions[faq.category]?.includes(userRole);
    if (!categoryVisible) return false;
    
    const matchesSearch = searchTerm === '' || 
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Agrupar por categoría
  const groupedFAQs = filteredFAQs.reduce((acc, faq, index) => {
    if (!acc[faq.category]) {
      acc[faq.category] = [];
    }
    acc[faq.category].push({ ...faq, globalIndex: FAQ_DATA.indexOf(faq) });
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="faq-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <HelpCircle className="h-6 w-6 text-primary" />
          Preguntas Frecuentes
        </h1>
        <p className="text-muted-foreground">Encuentra respuestas a las dudas más comunes sobre el sistema</p>
      </div>

      {/* Manual destacado */}
      <Card className="bg-gradient-to-r from-[#00B2A9]/10 to-[#008079]/10 border-primary/30">
        <CardContent className="flex flex-col md:flex-row items-center gap-4 p-6">
          <div className="p-4 bg-primary rounded-full">
            <BookOpen className="h-8 w-8 text-white" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="text-lg font-semibold">Manual de Usuario Completo</h3>
            <p className="text-muted-foreground">
              Descarga nuestra guía completa con instrucciones detalladas paso a paso para todas las funciones del sistema.
            </p>
          </div>
          <Button onClick={handleDownloadManual} className="bg-primary hover:bg-[#008079]" data-testid="download-manual-btn">
            <Download className="h-4 w-4 mr-2" />
            Descargar Manual PDF
          </Button>
        </CardContent>
      </Card>

      {/* Búsqueda y filtros */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar en preguntas frecuentes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="faq-search"
          />
        </div>
      </div>

      {/* Categorías */}
      <div className="flex flex-wrap gap-2">
        <Badge
          variant={selectedCategory === 'all' ? 'default' : 'outline'}
          className="cursor-pointer px-3 py-1"
          onClick={() => setSelectedCategory('all')}
        >
          Todas ({filteredFAQs.length})
        </Badge>
        {visibleCategories.map(cat => {
          const count = FAQ_DATA.filter(f => f.category === cat.id && categoryPermissions[f.category]?.includes(userRole)).length;
          return (
            <Badge
              key={cat.id}
              variant={selectedCategory === cat.id ? 'default' : 'outline'}
              className="cursor-pointer px-3 py-1 flex items-center gap-1"
              onClick={() => setSelectedCategory(cat.id)}
            >
              <cat.icon className="h-3 w-3" />
              {cat.title} ({count})
            </Badge>
          );
        })}
      </div>

      {/* Lista de preguntas */}
      {Object.keys(groupedFAQs).length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <AlertTriangle className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No se encontraron resultados</h3>
            <p className="text-muted-foreground">Intenta con otros términos de búsqueda</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {visibleCategories.filter(cat => groupedFAQs[cat.id]).map(category => (
            <Card key={category.id}>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <div className={`p-2 rounded-lg ${category.color}`}>
                    <category.icon className="h-4 w-4 text-white" />
                  </div>
                  {category.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {groupedFAQs[category.id].map((faq) => (
                  <div
                    key={faq.globalIndex}
                    className="border rounded-lg overflow-hidden"
                  >
                    <button
                      className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
                      onClick={() => toggleQuestion(faq.globalIndex)}
                      data-testid={`faq-question-${faq.globalIndex}`}
                    >
                      <span className="font-medium pr-4">{faq.question}</span>
                      {expandedQuestions[faq.globalIndex] ? (
                        <ChevronUp className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
                      )}
                    </button>
                    {expandedQuestions[faq.globalIndex] && (
                      <div className="px-4 pb-4 pt-0 text-muted-foreground border-t bg-muted/20">
                        <p className="pt-3">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Footer de ayuda */}
      <Card className="bg-muted/30">
        <CardContent className="flex flex-col md:flex-row items-center justify-between gap-4 p-6">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-8 w-8 text-primary" />
            <div>
              <h4 className="font-medium">¿No encontraste lo que buscabas?</h4>
              <p className="text-sm text-muted-foreground">Contacta al administrador del sistema para obtener ayuda adicional</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FAQPage;
